import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1601655781320-205e34c94eb1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwyfHxJbmRpYW4lMjBtYWxlJTIwdGVhY2hlciUyMHByb2Zlc3Npb25hbGVufDB8fHx8MTc4NTY2MzUyNnww&ixlib=rb-4.1.0&q=85";
const EASE = [0.22, 1, 0.36, 1];

const Line = ({ children, delay, className }) => (
  <div className="overflow-hidden">
    <motion.div
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ delay, duration: 1, ease: EASE }}
      className={className}
    >
      {children}
    </motion.div>
  </div>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const textY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const imgY = useTransform(scrollYProgress, [0, 1], [0, -90]);

  const go = (href) => {
    const el = document.querySelector(href);
    if (el && window.__lenis) window.__lenis.scrollTo(el, { offset: -70 });
  };

  return (
    <section ref={ref} className="relative flex min-h-screen flex-col justify-center overflow-hidden px-6 pt-28 md:px-12" data-testid="hero-section">
      <div className="pointer-events-none absolute right-0 top-24 hidden font-editorial text-[16rem] italic leading-none text-white/5 lg:block">
        est.
      </div>
      <div className="mx-auto grid w-full max-w-[1600px] grid-cols-1 items-end gap-12 lg:grid-cols-12">
        <motion.div style={{ y: textY }} className="lg:col-span-8">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.15, duration: 0.8 }}
            className="mb-8 text-xs font-medium uppercase tracking-[0.4em] text-brand"
            data-testid="hero-kicker"
          >
            ppacedemy.in — Learn · Build · Trade
          </motion.p>
          <h1 className="font-display text-[17vw] font-black uppercase leading-[0.85] tracking-tighter sm:text-[13vw] lg:text-[9.5rem]">
            <Line delay={0.25}>Learn.</Line>
            <Line delay={0.4} className="font-light text-zinc-500">Build.</Line>
            <Line delay={0.55} className="text-brand">Trade.</Line>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8, ease: EASE }}
            className="mt-10 max-w-xl text-base leading-relaxed text-zinc-400 md:text-lg"
            data-testid="hero-subtext"
          >
            Engineering courses, stock-market trading and school classes 1–12 across every board —
            taught by a mechanical engineer and MBA Finance mentor who trades what he teaches.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.8, ease: EASE }}
            className="mt-12 flex flex-wrap items-center gap-6"
          >
            <button
              data-testid="hero-browse-courses-button"
              onClick={() => go("#courses")}
              className="group flex items-center gap-3 bg-brand px-8 py-4 text-sm font-bold uppercase tracking-widest text-white transition-transform duration-300 hover:scale-95 focus-visible:ring-2 focus-visible:ring-brand"
            >
              Browse Courses
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </button>
            <button
              data-testid="hero-contact-link"
              onClick={() => go("#contact")}
              className="link-underline text-sm font-medium uppercase tracking-widest text-zinc-300"
            >
              Talk to the Mentor
            </button>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.3, duration: 1 }}
            className="mt-16 grid max-w-xl grid-cols-3 divide-x divide-white/10 border-y border-white/10"
            data-testid="hero-stats"
          >
            {[
              ["85+", "Courses"],
              ["4", "Disciplines"],
              ["1–12", "All Boards"],
            ].map(([num, label]) => (
              <div key={label} className="px-6 py-5">
                <div className="font-display text-3xl font-black tracking-tighter md:text-4xl">{num}</div>
                <div className="mt-1 text-xs uppercase tracking-widest text-zinc-500">{label}</div>
              </div>
            ))}
          </motion.div>
        </motion.div>
        <motion.div style={{ y: imgY }} className="relative lg:col-span-4">
          <motion.div
            initial={{ scaleY: 0 }}
            animate={{ scaleY: 1 }}
            transition={{ delay: 0.7, duration: 1.1, ease: EASE }}
            className="origin-bottom overflow-hidden border border-white/10"
          >
            <motion.img
              initial={{ scale: 1.5 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.7, duration: 1.4, ease: EASE }}
              src={HERO_IMG}
              alt="Founder and lead mentor of PP Academy"
              className="aspect-[3/4] w-full object-cover grayscale-[30%]"
              data-testid="hero-mentor-image"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 1.4, duration: 0.8, ease: EASE }}
            className="absolute -bottom-6 -left-6 border border-white/10 bg-[#111111] px-6 py-4"
          >
            <div className="font-editorial text-lg italic text-zinc-300">"Any textbook. Any board."</div>
            <div className="mt-1 text-xs uppercase tracking-widest text-brand">The Mentor's Promise</div>
          </motion.div>
        </motion.div>
      </div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <ArrowDown size={18} className="animate-bounce text-zinc-600" />
      </motion.div>
    </section>
  );
};
